import { RATE_CARD_TYPE_PRICING, TMultiRatePrice, TRateCard } from '@types';

type TAnyPrice = TRateCard | TMultiRatePrice | null | undefined;

const MULTI_RATE_METHODS: string[] = [
	RATE_CARD_TYPE_PRICING.PER_ITEM,
	RATE_CARD_TYPE_PRICING.PER_SPACE,
	RATE_CARD_TYPE_PRICING.COST_PER_KG
];

/** Whether the method is one only v2 knows. */
export const isMultiRateMethod = (method?: string | null): boolean => !!method && MULTI_RATE_METHODS.includes(method);

/** Whether a marked booking form still prices on v2: it names no method (v2 picks) or a v2 one. An admin naming Full Load or Tonnes in the row editor prices on v1. */
export const isMultiRateForm = (form: { pricing_method?: string | null; is_multi_rate?: boolean }): boolean =>
	form.is_multi_rate === true && (!form.pricing_method || isMultiRateMethod(form.pricing_method));

/** Whether the payload prices on shipment-api v2: marked so (a null method lets v2 pick), or by a method only v2 knows. */
export const isMultiRatePayload = (payload?: { pricing_method?: string | null; is_multi_rate?: boolean } | null): boolean =>
	payload?.is_multi_rate === true || isMultiRateMethod(payload?.pricing_method);

/** A multi-rate payload as the v1 store takes it: switched on by `is_multi_rate`. */
export const toMultiRateStorePayload = <T extends { pricing_method?: string | null }>(payload: T): T & { is_multi_rate: true } =>
	({ ...payload, is_multi_rate: true });

/** A multi-rate price carries the shape flag and one item per cargo row, whatever its method. */
export const isMultiRatePrice = (price: TAnyPrice): price is TMultiRatePrice =>
	price?.isMultiRate === true && Array.isArray(price.items);

/** The card whose figures describe the whole price: a single-card price itself, or the one card a COST_PER_KG or PER_SPACE multi-rate price is priced on. */
export const singleCardOf = (price: TAnyPrice): TRateCard | null => {
	if (!price) return null;
	if (!isMultiRatePrice(price)) return price;
	return price.pricingMethod === RATE_CARD_TYPE_PRICING.COST_PER_KG || price.pricingMethod === RATE_CARD_TYPE_PRICING.PER_SPACE
		? price.items[0] ?? null
		: null;
};

/** Tells one listed price from another: rate card or lane, per freight mode (a lane prices every mode). */
export const priceKeyOf = (price: TAnyPrice): string | undefined =>
	price ? `${price.rateCardExternalId ?? price.laneId}:${price.freightMode}` : undefined;
